/**
 * Client-side Real-time Referral Code Validator & Normalizer
 * Validates in-browser without sending redundant network requests.
 */

export const RESERVED_REFERRAL_CODES = new Set([
  'ADMIN',
  'ADMINISTRATOR',
  'ROOT',
  'API',
  'CONVERSORA',
  'PARTNER',
  'PARTNERS',
  'SUPPORT',
  'HELP',
  'AUTH',
  'LOGIN',
  'SIGNUP',
  'SIGNIN',
  'REGISTER',
  'BILLING',
  'PAYMENT',
  'CHECKOUT',
  'SYSTEM',
  'OFFICIAL',
]);

export interface ClientValidationStatus {
  isValid: boolean;
  normalizedCode: string;
  errorMessage: string | null;
  ruleStatus: {
    length: boolean;
    characters: boolean;
    boundary: boolean;
    notReserved: boolean;
  };
}

export function validateReferralCodeInBrowser(rawCode: string): ClientValidationStatus {
  const trimmed = rawCode.trim();
  const normalized = trimmed.toUpperCase();

  const lengthOk = normalized.length >= 3 && normalized.length <= 24;
  const boundaryOk = normalized.length >= 2 ? /^[A-Z0-9].*[A-Z0-9]$/.test(normalized) : false;
  const charsOk = /^[A-Z0-9_-]+$/.test(normalized);
  const notReserved = !RESERVED_REFERRAL_CODES.has(normalized);

  let errorMessage: string | null = null;

  if (!trimmed) {
    errorMessage = 'Referral code is required.';
  } else if (normalized.length < 3) {
    errorMessage = 'Too short: Must be at least 3 characters.';
  } else if (normalized.length > 24) {
    errorMessage = 'Too long: Cannot exceed 24 characters.';
  } else if (!charsOk) {
    errorMessage = 'Invalid characters: Only letters, numbers, hyphens, and underscores are allowed.';
  } else if (!boundaryOk) {
    errorMessage = 'Must start and end with a letter or number.';
  } else if (!notReserved) {
    errorMessage = `"${normalized}" is a reserved system keyword. Please choose another code.`;
  }

  const isValid = lengthOk && boundaryOk && charsOk && notReserved;

  return {
    isValid,
    normalizedCode: normalized,
    errorMessage,
    ruleStatus: {
      length: lengthOk,
      characters: charsOk,
      boundary: boundaryOk,
      notReserved,
    },
  };
}
